import { useCallback, useMemo, useState } from "react"
import { useParams } from "react-router-dom"
import format from "date-fns/format"
import isValid from "date-fns/isValid"
import sub from "date-fns/sub"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import type {} from "@mui/x-date-pickers/themeAugmentation"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import { LocalizationProvider } from "@mui/x-date-pickers"

import { useModal, useModalActions } from "@/shared/hooks"
import { ModalType, RouteParams } from "@/shared/types"

import { Box, Button, Copy, Flex, FormInput, FormSelect, Stack, Title } from "@/shared/components"
import { GeneralModal } from "@/modals"
import { getOrdersCsvFn } from "@/api/ordersApi"

const CUSTOM_RANGE = "custom"

const dateRangeOptions = [
  { value: "30", label: "Last 30 days" },
  { value: "60", label: "Last 60 days" },
  { value: "90", label: "Last 90 days" },
  { value: "180", label: "Last 180 days" },
  { value: CUSTOM_RANGE, label: "Custom date" },
]

const datePickerTheme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "var(--colors-theme-w-n8)",
          color: "var(--colors-theme-b-n3)",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--colors-theme-n3-n7)",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--colors-theme-yl-n3)",
            borderWidth: "1px",
          },
        },
        notchedOutline: {
          borderColor: "var(--colors-theme-n3-n7)",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "var(--colors-theme-n6-n5)",
          "&.Mui-focused": {
            color: "var(--colors-theme-b-n3)",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: "var(--space-48)",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "var(--colors-theme-b-n3)",
          borderRadius: "0",
          "&:hover": {
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          "&:not(.Mui-disabled)": {
            backgroundColor: "var(--colors-theme-w-n8)",
            color: "var(--colors-theme-b-n3)",
          },
          "&.Mui-selected": {
            backgroundColor: "var(--colors-brand-yellow-primary)",
            "&:hover": {
              backgroundColor: "var(--colors-brand-yellow-primary)",
            },
            "&:focus": {
              backgroundColor: "var(--colors-brand-yellow-primary)",
            },
          },
        },
        today: {
          "&:not(.Mui-selected)": {
            border: "1px solid var(--colors-theme-b-n3)",
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
  },
})

export const ExportOrdersModal = () => {
  const { organizationId } = useParams<keyof RouteParams>() as RouteParams
  const [email, setEmail] = useState("")
  const [dateRange, setDateRange] = useState("90")
  const [customDate, setCustomDate] = useState<Date | null>(null)
  const { modalProps } = useModal(ModalType.ExportOrdersModal)
  const { close } = useModalActions()

  const selectedOption = useMemo(
    () => dateRangeOptions.find((option) => option.value === dateRange) ?? dateRangeOptions[2],
    [dateRange],
  )

  const dateFrom = useMemo(() => {
    if (dateRange === CUSTOM_RANGE) {
      return customDate && isValid(customDate) ? customDate : null
    }

    return sub(new Date(), { days: Number(dateRange) })
  }, [customDate, dateRange])

  const resetForm = useCallback(() => {
    setEmail("")
    setDateRange("90")
    setCustomDate(null)
  }, [])

  const handleCustomDateChange = (value: Date | null) => {
    // Keep Invalid Date out of state so format()/API never throw while typing.
    if (value === null || isValid(value)) {
      setCustomDate(value)
    }
  }

  const handleCancelClick = () => {
    resetForm()
    close(ModalType.ExportOrdersModal)
  }

  const handleContinueClick = useCallback(async () => {
    if (!email.trim() || !dateFrom || !isValid(dateFrom)) return

    await getOrdersCsvFn(organizationId, email.trim(), dateFrom)
    resetForm()
    close(ModalType.ExportOrdersModal)
  }, [close, dateFrom, email, organizationId, resetForm])

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
  const isEmailValid = Boolean(email.trim() && isValidEmail(email.trim()))
  const isDateValid = dateRange !== CUSTOM_RANGE || Boolean(customDate && isValid(customDate))
  const canSubmit = isEmailValid && isDateValid

  return (
    <GeneralModal
      {...modalProps}
      closeButton={{ onClick: handleCancelClick }}
      // Radix locks body pointer-events while open. MUI calendar portals to body, so outside
      // clicks must not dismiss (same pattern as CreateCarrierModal). Close via Cancel / X / Esc.
      onInteractOutside={(e) => {
        e?.preventDefault()
      }}
      onEscapeKeyDown={handleCancelClick}
      footer={
        <Flex justify="end" css={{ gap: "$16", paddingBottom: "$8" }}>
          <Button onClick={handleContinueClick} disabled={!canSubmit}>
            OK
          </Button>
          <Button action="secondary" onClick={handleCancelClick}>
            Cancel
          </Button>
        </Flex>
      }
    >
      <Stack space={12} css={{ padding: "$64 $24 $0" }}>
        <Title as="h3" scale={2} color="system-black">
          Export Orders
        </Title>

        <Copy scale={10} color="theme-n6-n5">
          Please provide an email address. The CSV file with orders will be sent to this email in ~5 minutes.
        </Copy>

        <FormInput
          type="email"
          placeholder="Enter email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          label="Email address"
          labelProps={{ required: true }}
          description="Email address"
        />

        <FormSelect
          id="exportDateRange"
          name="exportDateRange"
          label="Date from"
          labelProps={{ required: true }}
          isDisplayValueDifferent
          value={selectedOption}
          options={dateRangeOptions}
          onValueChange={(value) => {
            setDateRange(value as string)
            setCustomDate(null)
          }}
        />

        {dateRange === CUSTOM_RANGE && (
          <Box css={{ width: "100%" }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <ThemeProvider theme={datePickerTheme}>
                <DatePicker
                  label="Select date from"
                  value={customDate}
                  maxDate={new Date()}
                  onChange={handleCustomDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                    // Body has pointer-events:none under Radix Dialog; portaled popper must opt back in.
                    popper: {
                      sx: {
                        pointerEvents: "auto",
                      },
                    },
                  }}
                />
              </ThemeProvider>
            </LocalizationProvider>
          </Box>
        )}

        <Copy scale={10} color="theme-n6-n5" css={{ paddingBottom: "$8" }}>
          {dateFrom && isValid(dateFrom)
            ? `Orders created from ${format(dateFrom, "MMM d, yyyy")} will be exported with market rates.`
            : "Select a start date. Orders created from that date will be exported with market rates."}
        </Copy>
      </Stack>
    </GeneralModal>
  )
}
